import * as THREE from 'three';
import { registerShot } from '../core/Shots.js';
import { bestRunway, runwayLength, forwardFromYaw, rightFromYaw } from './Runway.js';

/**
 * Deterministic player shots for the capture harness.
 *
 * Every shot: resolves a standing spot from `ctx.level.spawnPoints`, steps the
 * 120 Hz simulation with scripted input to the exact moment we want, then
 * freezes the system so the harness' settle frames cannot drift the pose.
 *
 * All of them are level-agnostic — the slide shot picks the longest walkable
 * runway from the spawn, and the mantle shot searches the real geometry for a
 * mantleable ledge rather than hard-coding one.
 */

const DEG = Math.PI / 180;
const _v = new THREE.Vector3();
const _down = new THREE.Vector3(0, -1, 0);
const _f = new THREE.Vector3();
const _r = new THREE.Vector3();

function spawnFeet(sys) {
  return sys.movement.spawnFeet(new THREE.Vector3());
}

/** Is there floor under this spot? Keeps shots out of the void on any level. */
function hasGround(sys, feet) {
  _v.set(feet.x, feet.y + 1.2, feet.z);
  const h = sys.adapter.raycast(_v, _down, 6);
  return !!h && h.point.y > feet.y - 3.0;
}

/**
 * A spot with `need` metres of runway ahead of it. Starts at the spawn facing
 * its longest open direction, then walks *backwards* along that direction so
 * the player has room to build up speed.
 */
function runUpSpot(sys, backUp, flatTol = 0.10) {
  const feet = spawnFeet(sys);
  /* flat ground only: sprinting up stairs never reaches slide speed */
  const yaw = bestRunway(sys.adapter, sys.cfg, feet, 64, 30, sys._spawnYaw(), flatTol).yaw;
  forwardFromYaw(yaw, _f);
  const behind = runwayLength(sys.adapter, sys.cfg, feet, yaw + Math.PI, backUp + 1, 0.35, flatTol);
  const back = Math.min(backUp, Math.max(0, behind - 0.6));
  const start = feet.clone().addScaledVector(_f, -back);
  const ok = back > 0.5 && hasGround(sys, start);
  const from = ok ? start : feet;
  return { feet: from, yaw, runway: runwayLength(sys.adapter, sys.cfg, from, yaw, 30, 0.35, flatTol) };
}

/**
 * Search the level near the spawn for something mantleable.
 * Returns `{feet, yaw, kind, height}` placing the player right in front of it.
 */
function findLedge(sys) {
  const mv = sys.movement;
  const cfg = sys.cfg;
  const origin = spawnFeet(sys);
  const baseYaw = sys._spawnYaw();
  const saveFeet = mv.feet.clone();
  const saveYaw = mv.yaw;

  let best = null;
  /* fan outwards from the spawn facing, alternating left/right, and walk a
     little way down each heading so we also find ledges further in */
  outer:
  for (let i = 0; i < 48; i++) {
    const k = Math.ceil(i / 2) * (i % 2 ? 1 : -1);
    const yaw = baseYaw + k * (7.5 * DEG);
    forwardFromYaw(yaw, _f);
    for (const startOff of [0, 4, 8, 12]) {
      const from = origin.clone().addScaledVector(_f, startOff);
      if (startOff > 0 && !hasGround(sys, from)) continue;
      _v.set(from.x, from.y + 1.1, from.z);
      const hit = sys.adapter.raycast(_v, _f, 16);
      if (!hit) continue;
      const dist = hit.distance - (cfg.radius + 0.28);
      if (dist < 0.15) continue;
      const feet = from.clone().addScaledVector(_f, dist);
      if (!hasGround(sys, feet)) continue;
      mv.feet.copy(feet);
      mv.yaw = yaw;
      const led = mv._probeLedge(false);
      if (led && led.height > 0.55) {
        best = { feet, yaw, kind: led.kind, height: led.height };
        break outer;
      }
    }
  }

  mv.feet.copy(saveFeet);
  mv.yaw = saveYaw;
  return best;
}

export function registerPlayerShots(sys) {
  const cfg = sys.cfg;

  registerShot('player_stand', () => {
    const feet = spawnFeet(sys);
    sys.place(feet, sys._spawnYaw(), -4 * DEG);
    sys.run(0.55);
    sys.freeze(true);
  }, { desc: 'first-person eye view, standing at the spawn (eye 1.65 m)', settle: 8 });

  registerShot('player_crouch', () => {
    const feet = spawnFeet(sys);
    sys.place(feet, sys._spawnYaw(), -4 * DEG);
    sys.run(0.90, () => ({ crouch: true }));
    sys.freeze(true);
  }, { desc: 'same spot, crouched — eye drops 1.65 m to 0.95 m', settle: 8 });

  registerShot('player_slide', () => {
    const spot = runUpSpot(sys, 9);
    sys.place(spot.feet, spot.yaw, -2 * DEG);
    /* accelerate to sprint, but leave room to actually slide */
    const sprintUp = Math.max(0.5, Math.min(2.0, (spot.runway - 5) / cfg.speedSprint));
    sys.runUntil(() => sys.movement.speed >= cfg.slideMinSpeed + 0.7 || sys.movement.hitWall,
      sprintUp, () => ({ forward: 1, sprint: true }));
    /* drop into the slide */
    sys._runStep({ forward: 1, sprint: true, crouch: true, crouchEdge: true });
    /* hold ~1/3 of the way in, where the roll and the dip are strongest */
    sys.run(0.30, () => ({ forward: 1, crouch: true }));
    sys.freeze(true);
  }, { desc: 'mid-slide — camera roll and the eye dipped to 0.78 m', settle: 8 });

  registerShot('player_mantle', () => {
    const led = findLedge(sys);
    if (!led) {
      sys.place(spawnFeet(sys), sys._spawnYaw(), -8 * DEG);
      sys.run(0.4);
      sys.freeze(true);
      return;
    }
    sys.place(led.feet, led.yaw, -10 * DEG);
    /* walk into the ledge, then jump — the controller converts it to a mantle */
    sys.runUntil(() => sys.movement.hitWall, 1.6, () => ({ forward: 1 }));
    sys.runUntil(() => sys.movement.mantling, 0.8, () => ({ forward: 1, jump: true, jumpEdge: true }));
    if (sys.movement.mantling) {
      /* freeze at 45% of the arc: over the lip, hands planted */
      sys.run(Math.max(0, 0.45 * sys.movement.mantle.duration - 1 / 120));
    }
    sys.freeze(true);
  }, { desc: 'mid-mantle over a ledge, hand IK targets published', settle: 8 });

  registerShot('player_air', () => {
    const feet = spawnFeet(sys);
    sys.place(feet, sys._spawnYaw(), -52 * DEG);
    sys._runStep({ jump: true, jumpEdge: true });
    /* stop just short of the 0.55 m apex, before the fall starts */
    sys.run(0.20);
    sys.freeze(true);
  }, { desc: 'mid-jump near the apex, looking down', settle: 8 });
}

export default registerPlayerShots;
